import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Users, Clock, Kanban, FileText, QrCode, Calendar, Send, Upload, BarChart3, Layers, Settings, Bell, LogOut, Menu, X, Plus, ChevronDown } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { LogoMark } from "../Logo";
import api from "../../lib/api";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/contacts", label: "Contacts", icon: Users },
  { to: "/app/follow-ups", label: "Follow-Ups", icon: Clock },
  { to: "/app/pipelines", label: "Pipelines", icon: Kanban },
  { to: "/app/forms", label: "Form Builder", icon: FileText },
  { to: "/app/qr-codes", label: "QR Codes", icon: QrCode },
  { to: "/app/events", label: "Events", icon: Calendar },
  { to: "/app/campaigns", label: "Campaigns", icon: Send },
  { to: "/app/imports", label: "Data Import", icon: Upload },
  { to: "/app/reports", label: "Reports", icon: BarChart3 },
  { to: "/app/integrations", label: "Integrations", icon: Layers },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

function OrgSwitcher() {
  const { org, switchOrg } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/auth/organizations").then(({ data }) => setOrgs(data.items)).catch(() => {}); }, []);
  const pick = async (id) => { setOpen(false); if (id === org?.id) return; await switchOrg(id); window.location.href = "/app/dashboard"; };
  if (orgs.length <= 1) return <p className="font-display font-bold text-slate-900 text-sm leading-tight mt-0.5" data-testid="crm-org-name">{org?.name || "—"}</p>;
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} data-testid="crm-org-switcher" className="font-display font-bold text-slate-900 text-sm leading-tight mt-0.5 flex items-center gap-1.5 hover:text-alto-red">
        {org?.name || "—"} <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute left-0 mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50">
          {orgs.map((o) => (
            <button key={o.id} onClick={() => pick(o.id)} data-testid={`org-option-${o.id}`} className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 ${o.active ? "text-alto-red font-semibold" : "text-slate-700"}`}>
              {o.name}<span className="block text-xs text-slate-400">{o.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CrmLayout({ children }) {
  const { user, org, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const [notifs, setNotifs] = useState([]);

  const loadNotifs = () => api.get("/notifications").then(({ data }) => { setNotifs(data.items); setUnread(data.unread); }).catch(() => {});
  useEffect(() => { loadNotifs(); const t = setInterval(loadNotifs, 60000); return () => clearInterval(t); }, []);

  const doLogout = async () => { await logout(); navigate("/app/login"); };
  const openNotif = () => { setShowNotif(!showNotif); if (!showNotif && unread) api.post("/notifications/read").then(() => setUnread(0)); };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 z-40 h-screen w-64 bg-alto-black text-white flex flex-col shrink-0 transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/10">
          <Link to="/app/dashboard" className="flex items-center gap-2.5" data-testid="crm-logo">
            <LogoMark size={28} mono="#FFFFFF" />
            <span className="font-display font-extrabold text-sm">AllTo<span className="text-alto-red">MAID</span>It</span>
          </Link>
          <button className="lg:hidden text-neutral-400" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 scrollbar-thin">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`sidebar-${n.label.replace(/\s+/g,'-').toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-white/10 text-white border-l-2 border-alto-red" : "text-neutral-400 hover:text-white hover:bg-white/5"}`}>
              <n.icon size={18} />{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={doLogout} data-testid="crm-logout" className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-neutral-400 hover:text-white hover:bg-white/5 w-full">
            <LogOut size={18} /> Sign out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-600" onClick={() => setOpen(true)} data-testid="crm-menu-toggle"><Menu size={22} /></button>
            <div>
              <p className="text-xs text-slate-400 leading-none">Workspace</p>
              <OrgSwitcher />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/app/contacts?quick=1" className="hidden sm:inline-flex btn-red font-semibold px-3.5 py-2 rounded-md text-sm items-center gap-1.5" data-testid="crm-quick-add"><Plus size={15} /> Add Contact</Link>
            <div className="relative">
              <button onClick={openNotif} className="relative p-2 text-slate-500 hover:text-slate-900" data-testid="crm-notifications">
                <Bell size={20} />
                {unread > 0 && <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-alto-red rounded-full" />}
              </button>
              {showNotif && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-slate-100 font-semibold text-sm text-slate-900">Notifications</div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifs.length === 0 ? <p className="text-sm text-slate-400 p-4 text-center">No notifications yet.</p> :
                      notifs.map((n) => (
                        <div key={n.id} className="px-4 py-3 border-b border-slate-50 last:border-0">
                          <p className="text-sm font-medium text-slate-800">{n.title}</p>
                          <p className="text-xs text-slate-500">{n.body}</p>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
            <div className="w-9 h-9 rounded-full bg-alto-black text-white flex items-center justify-center text-sm font-bold" title={user?.name}>
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
